import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Section, Stepper, MiniTimer, FolioBadge } from "@/components/domain/Bits";
import TicketSelector from "@/components/domain/TicketSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { money, num, pad, padFor, raffleImg, onImgError } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Landmark, Copy, CheckCircle2, Upload, ShieldAlert,
  FileCheck2, Clock, Loader2, PartyPopper,
} from "lucide-react";

const STEPS = ["Boletos", "Tus datos", "Verifica", "Pago", "Comprobante", "Listo"];

export default function Purchase() {
  const { id } = useParams();
  const nav = useNavigate();
  const [raffle, setRaffle] = useState(null);
  const [taken, setTaken] = useState([]);
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState([]);
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({ name: "", lastname: "", email: "", whatsapp: "", city: "", state: "", terms: false });
  const [reservation, setReservation] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);

  useEffect(() => {
    api.get(`/raffles/${id}`).then((r) => setRaffle(r.data));
    api.get(`/raffles/${id}/tickets/taken`).then((r) => setTaken(r.data.taken));
    api.get("/tenant/atlas").then((r) => setBanks(r.data.bank_accounts));
  }, [id]);

  useEffect(() => {
    if (selected.length > 0) api.post("/quote", { raffle_id: id, qty: selected.length }).then((r) => setQuote(r.data));
    else setQuote(null);
  }, [selected, id]);

  if (!raffle) return <Section className="py-20"><Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-300" /></Section>;

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiado"); };

  const createReservation = async () => {
    setLoading(true);
    try {
      const r = await api.post("/reservations", {
        raffle_id: id, numbers: selected,
        customer: { name: form.name, lastname: form.lastname, email: form.email, whatsapp: form.whatsapp, city: form.city, state: form.state },
      });
      setReservation(r.data);
      setStep(3);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.conflicts) {
        toast.error(`Boletos ya no disponibles: ${d.conflicts.map((n) => padFor(raffle)(n)).join(", ")}`);
        setSelected((prev) => prev.filter((n) => !d.conflicts.includes(n)));
        setTaken((prev) => [...prev, ...d.conflicts]);
        setStep(0);
      } else toast.error(typeof d === "string" ? d : "No se pudo crear la reserva");
    } finally { setLoading(false); }
  };

  const submitProof = async () => {
    if (!proofFile) { toast.error("Selecciona un archivo"); return; }
    setUploadPct(0);
    const fd = new FormData();
    fd.append("folio", reservation.folio);
    fd.append("token", reservation.token);
    fd.append("amount", reservation.total);
    fd.append("reference", reservation.folio);
    fd.append("file", proofFile);
    try {
      await api.post("/reservations/proof", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setUploadPct(e.total ? Math.round((e.loaded / e.total) * 100) : 50),
      });
      setUploadPct(100);
      setStep(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir el comprobante");
      setUploadPct(0);
    }
  };

  const canNext = () => {
    if (step === 0) return selected.length > 0;
    if (step === 1) return form.name && form.email && form.whatsapp && form.terms;
    return true;
  };

  return (
    <Section className="py-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm text-slate-400"><Link to={`/sorteos/${id}`} className="hover:text-brand">{raffle.name}</Link></div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Comprar boletos</h1>
        </div>
        {reservation && step >= 3 && step < 5 && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <Clock className="h-4 w-4 text-amber-600" /><span className="text-xs text-amber-700">Reserva vence en</span><MiniTimer target={reservation.expires_at} />
          </div>
        )}
      </div>

      <Stepper steps={STEPS} current={step} />

      <div className="mt-8 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }}>
              {step === 0 && <TicketSelector raffle={raffle} taken={taken} selected={selected} setSelected={setSelected} />}

              {step === 1 && (
                <div className="rounded-xl border border-slate-200 bg-white p-6">
                  <h3 className="text-lg font-bold text-slate-900">¿Cómo te llamas?</h3>
                  <p className="mt-1 text-sm text-slate-500">Usaremos estos datos para identificar tu participación.</p>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <Field label="Nombre" v={form.name} set={(v) => setForm({ ...form, name: v })} testid="f-name" />
                    <Field label="Apellidos" v={form.lastname} set={(v) => setForm({ ...form, lastname: v })} testid="f-lastname" />
                    <Field label="Correo" type="email" v={form.email} set={(v) => setForm({ ...form, email: v })} testid="f-email" />
                    <Field label="WhatsApp" v={form.whatsapp} set={(v) => setForm({ ...form, whatsapp: v })} testid="f-whatsapp" />
                    <Field label="Ciudad" v={form.city} set={(v) => setForm({ ...form, city: v })} testid="f-city" />
                    <Field label="Estado" v={form.state} set={(v) => setForm({ ...form, state: v })} testid="f-state" />
                  </div>
                  <label className="mt-5 flex items-start gap-3 text-sm text-slate-600">
                    <Checkbox checked={form.terms} onCheckedChange={(v) => setForm({ ...form, terms: !!v })} data-testid="f-terms" />
                    Acepto las bases del sorteo y el aviso de privacidad.
                  </label>
                </div>
              )}

              {step === 2 && (
                <div className="rounded-xl border border-slate-200 bg-white p-6">
                  <h3 className="text-lg font-bold text-slate-900">Verifica tu información</h3>
                  <dl className="mt-5 divide-y divide-slate-100 text-sm">
                    {[["Participante", `${form.name} ${form.lastname}`], ["Correo", form.email], ["WhatsApp", form.whatsapp], ["Ubicación", `${form.city}, ${form.state}`], ["Boletos", `${selected.length} seleccionados`]].map(([k, v]) => (
                      <div key={k} className="flex justify-between py-2.5"><dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-900">{v}</dd></div>
                    ))}
                  </dl>
                  <div className="mt-4 flex max-h-28 flex-wrap gap-1.5 overflow-y-auto rounded-lg bg-slate-50 p-3">
                    {selected.slice().sort((a,b)=>a-b).map((n) => <span key={n} className="rounded bg-white px-2 py-1 font-mono text-xs font-semibold text-slate-700 shadow-sm">{padFor(raffle)(n)}</span>)}
                  </div>
                </div>
              )}

              {step === 3 && reservation && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                    <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /><span className="font-semibold">Reserva creada</span></div>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <span className="text-sm text-slate-600">Tu folio:</span><FolioBadge folio={reservation.folio} />
                      <Button size="sm" variant="outline" onClick={() => copy(reservation.folio)}><Copy className="mr-1 h-3.5 w-3.5" />Copiar</Button>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-6">
                    <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Landmark className="h-5 w-5 text-brand" />Realiza tu transferencia</h3>
                    <p className="mt-1 text-sm text-slate-500">Transfiere el monto exacto a una de nuestras cuentas oficiales.</p>
                    <div className="mt-4 rounded-lg bg-slate-900 p-4 text-white">
                      <div className="text-xs text-slate-400">Monto exacto a transferir</div>
                      <div className="font-mono text-3xl font-bold">{money(reservation.total)}</div>
                    </div>
                    <div className="mt-4 space-y-3">
                      {banks.map((b) => (
                        <div key={b.id} className="rounded-lg border border-slate-200 p-4">
                          <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">{b.bank}</span><span className="text-xs text-emerald-600">Cuenta oficial ✓</span></div>
                          <div className="mt-2 space-y-1.5 text-sm">
                            <Row label="Titular" value={b.holder} />
                            <Row label="CLABE" value={b.clabe} mono onCopy={() => copy(b.clabe)} />
                            <Row label="Cuenta" value={b.account} mono onCopy={() => copy(b.account)} />
                            <Row label="Referencia" value={reservation.folio} mono onCopy={() => copy(reservation.folio)} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />Ningún vendedor está autorizado para solicitar depósitos en cuentas personales. Verifica siempre en el Centro de Transparencia.
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="rounded-xl border border-slate-200 bg-white p-6">
                  <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Upload className="h-5 w-5 text-brand" />Sube tu comprobante</h3>
                  <p className="mt-1 text-sm text-slate-500">Adjunta tu comprobante (JPG, PNG o PDF). Se almacena de forma privada.</p>
                  <label className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 py-12 text-center hover:border-brand" data-testid="proof-dropzone">
                    <FileCheck2 className="h-8 w-8 text-slate-300" />
                    <span className="text-sm font-medium text-slate-600">{proofFile ? proofFile.name : "Haz clic para seleccionar tu comprobante"}</span>
                    <span className="text-xs text-slate-400">Máx. 10 MB · JPG, PNG, PDF</span>
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setProofFile(e.target.files[0])} />
                  </label>
                  {uploadPct > 0 && (
                    <div className="mt-4"><div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-brand transition-all" style={{ width: `${uploadPct}%` }} /></div></div>
                  )}
                  <div className="mt-4 rounded-lg bg-blue-50 p-3 text-xs text-blue-800">
                    Un comprobante enviado no significa que el pago fue aprobado. Nuestro equipo validará tu pago y te avisaremos por correo.
                  </div>
                </div>
              )}

              {step === 5 && reservation && (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }} className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-50">
                    <PartyPopper className="h-8 w-8 text-emerald-600" />
                  </motion.div>
                  <h3 className="mt-4 text-2xl font-bold text-slate-900">¡Comprobante recibido!</h3>
                  <p className="mx-auto mt-2 max-w-md text-slate-500">Tu pago está en revisión. Te notificaremos por correo cuando se confirmen tus boletos.</p>
                  <div className="mt-5 flex items-center justify-center gap-3"><FolioBadge folio={reservation.folio} /></div>
                  <div className="mt-6 flex flex-wrap justify-center gap-3">
                    <Button asChild className="rounded-full bg-brand hover:bg-brand-hover" data-testid="go-lookup">
                      <Link to={`/consulta?folio=${reservation.folio}&contact=${form.email}`}>Ver mi participación</Link>
                    </Button>
                    <Button asChild variant="outline" className="rounded-full"><Link to="/sorteos">Ver más sorteos</Link></Button>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {step < 5 && (
            <div className="mt-6 flex items-center justify-between">
              <Button variant="ghost" onClick={() => step === 0 ? nav(`/sorteos/${id}`) : setStep(step - 1)} data-testid="step-back">
                <ArrowLeft className="mr-1.5 h-4 w-4" />Atrás
              </Button>
              {step === 2 ? (
                <Button onClick={createReservation} disabled={loading} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="create-reservation">
                  {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Crear reserva y generar folio
                </Button>
              ) : step === 3 ? (
                <Button onClick={() => setStep(4)} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="go-proof">Ya transferí, subir comprobante <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
              ) : step === 4 ? (
                <Button onClick={submitProof} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="submit-proof"><Upload className="mr-1.5 h-4 w-4" />Enviar comprobante</Button>
              ) : (
                <Button onClick={() => setStep(step + 1)} disabled={!canNext()} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="step-next">Continuar <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
              )}
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="font-semibold text-slate-900">Resumen</h3>
            <div className="mt-4 flex items-center gap-3">
              <img src={raffleImg(raffle)} onError={onImgError} alt="" className="h-14 w-20 rounded-lg object-cover" />
              <div><div className="text-sm font-semibold text-slate-900 line-clamp-1">{raffle.name}</div><div className="text-xs text-slate-500">{money(raffle.price)} c/u</div></div>
            </div>
            <dl className="mt-5 space-y-2.5 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Boletos</dt><dd className="font-mono font-semibold">{num(selected.length)}</dd></div>
              {quote?.bonus_tickets > 0 && <div className="flex justify-between text-emerald-600"><dt>Boletos de regalo</dt><dd className="font-mono font-semibold">+{quote.bonus_tickets}</dd></div>}
              <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd className="font-mono">{money(quote?.subtotal || 0)}</dd></div>
              {quote?.discount > 0 && <div className="flex justify-between text-emerald-600"><dt>Descuento</dt><dd className="font-mono">-{money(quote.discount)}</dd></div>}
            </dl>
            {quote?.applied_promo && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">{quote.applied_promo.label}</div>
            )}
            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
              <span className="font-semibold text-slate-900">Total</span>
              <span className="font-mono text-2xl font-bold text-slate-900">{money(quote?.total || 0)}</span>
            </div>
            {quote?.saving > 0 && <div className="mt-2 text-center text-xs font-medium text-emerald-600">Ahorras {money(quote.saving)} con la promoción</div>}
          </div>
        </div>
      </div>
    </Section>
  );
}

const Field = ({ label, v, set, type = "text", testid }) => (
  <div><Label className="text-xs">{label}</Label><Input type={type} value={v} onChange={(e) => set(e.target.value)} className="mt-1.5" data-testid={testid} /></div>
);

const Row = ({ label, value, mono, onCopy }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-slate-500">{label}</span>
    <span className="flex items-center gap-2">
      <span className={mono ? "font-mono font-semibold text-slate-900" : "font-medium text-slate-900"}>{value}</span>
      {onCopy && <button onClick={onCopy} className="text-slate-400 hover:text-brand"><Copy className="h-3.5 w-3.5" /></button>}
    </span>
  </div>
);
